import React, { useState, useEffect } from 'react';
import {
  Server,
  Plus,
  Settings,
  TestTube,
  FileText,
  Database,
  Cloud,
  Shield,
  Activity,
  Users,
  FolderOpen,
  Upload,
  Download,
  Trash2,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  Eye,
  EyeOff,
} from 'lucide-react';
import Button from '../components/Button';
import Card from '../components/Card';
import { apiUrl } from '../config';

interface Connector2 {
  connectorId: string;
  awsConnectorId?: string;
  name: string;
  host: string;
  port?: number;
  username?: string;
  s3Bucket?: string;
  s3Prefix?: string;
  status?: 'active' | 'inactive' | 'error';
  lastTested?: string;
  tags?: Record<string, string>;
}

interface FileItem {
  name: string;
  type: 'file' | 'directory';
  size?: number;
  modified?: string;
  path: string;
}

export default function Connector2Page() {
  const [connectors, setConnectors] = useState<Connector2[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedConnector, setSelectedConnector] = useState<string | null>(null);
  const [showFileBrowser, setShowFileBrowser] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['overview', 'connectors'])
  );
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    loadConnectors();
  }, []);

  const loadConnectors = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔍 Loading connectors for Connector2 page...');
      const response = await fetch(apiUrl('sftp-connectors'));

      if (!response.ok) {
        throw new Error(`Failed to load connectors: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Connectors loaded:', data);

      const connectorsList: Connector2[] = (data.connectors || []).map((connector: any) => ({
        connectorId: connector.connectorId,
        awsConnectorId: connector.awsConnectorId,
        name: connector.name,
        host: connector.host,
        port: connector.port || 22,
        username: connector.username,
        s3Bucket: connector.s3Bucket,
        s3Prefix: connector.s3Prefix,
        status: connector.status || 'active',
        lastTested: connector.lastTested,
        tags: connector.tags || {},
      }));

      setConnectors(connectorsList);
    } catch (err) {
      console.error('❌ Error loading connectors:', err);
      setError(err instanceof Error ? err.message : 'Failed to load connectors');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const testConnection = async (connectorId: string) => {
    try {
      console.log('🔍 Testing connection for connector:', connectorId);
      const response = await fetch(apiUrl(`sftp-connectors/${connectorId}/test-connection`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ connectorId }),
      });

      if (!response.ok) {
        throw new Error('Connection test failed');
      }

      const result = await response.json();
      console.log('✅ Connection test result:', result);

      // Refresh connectors to update status
      await loadConnectors();
    } catch (err) {
      console.error('❌ Error testing connection:', err);
      setError(err instanceof Error ? err.message : 'Connection test failed');
    }
  };

  const handleFileAction = async (action: string, connectorId: string) => {
    console.log(`🔍 File action: ${action} for connector: ${connectorId}`);

    switch (action) {
      case 'browse':
        setShowFileBrowser(connectorId);
        break;
      case 'upload':
        console.log('📤 Upload action - to be implemented');
        break;
      case 'download':
        console.log('📥 Download action - to be implemented');
        break;
      case 'sync':
        console.log('🔄 Sync action - to be implemented');
        break;
      default:
        console.log('❓ Unknown action:', action);
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active':
        return 'text-green-400';
      case 'inactive':
        return 'text-yellow-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-slate-400';
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'active':
        return <Activity className='h-4 w-4' />;
      case 'inactive':
        return <Shield className='h-4 w-4' />;
      case 'error':
        return <Trash2 className='h-4 w-4' />;
      default:
        return <Server className='h-4 w-4' />;
    }
  };

  return (
    <div className='min-h-screen bg-slate-900 text-white'>
      {/* Header */}
      <div className='bg-slate-800 border-b border-slate-700'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex items-center justify-between h-16'>
            <div className='flex items-center gap-3'>
              <Database className='h-8 w-8 text-blue-400' />
              <div>
                <h1 className='text-xl font-bold'>Connector 2.0</h1>
                <p className='text-sm text-slate-400'>Advanced SFTP Management</p>
              </div>
            </div>
            <div className='flex items-center gap-2'>
              <Button onClick={() => setShowAdvanced(!showAdvanced)} variant='outline'>
                {showAdvanced ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
              </Button>
              <Button onClick={loadConnectors} disabled={loading}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* Overview Section */}
        <Card className='mb-6'>
          <div
            className='flex items-center justify-between cursor-pointer'
            onClick={() => toggleSection('overview')}
          >
            <h2 className='text-lg font-semibold flex items-center gap-2'>
              <Cloud className='h-5 w-5 text-blue-400' />
              System Overview
            </h2>
            {expandedSections.has('overview') ? (
              <ChevronDown className='h-5 w-5' />
            ) : (
              <ChevronRight className='h-5 w-5' />
            )}
          </div>

          {expandedSections.has('overview') && (
            <div className='mt-4 grid grid-cols-1 md:grid-cols-4 gap-4'>
              <div className='bg-slate-800 p-4 rounded-lg'>
                <div className='flex items-center gap-2 mb-2'>
                  <Server className='h-5 w-5 text-green-400' />
                  <span className='text-sm font-medium'>Active Connectors</span>
                </div>
                <div className='text-2xl font-bold text-green-400'>
                  {connectors.filter(c => c.status === 'active').length}
                </div>
              </div>

              <div className='bg-slate-800 p-4 rounded-lg'>
                <div className='flex items-center gap-2 mb-2'>
                  <Shield className='h-5 w-5 text-yellow-400' />
                  <span className='text-sm font-medium'>Inactive</span>
                </div>
                <div className='text-2xl font-bold text-yellow-400'>
                  {connectors.filter(c => c.status === 'inactive').length}
                </div>
              </div>

              <div className='bg-slate-800 p-4 rounded-lg'>
                <div className='flex items-center gap-2 mb-2'>
                  <Trash2 className='h-5 w-5 text-red-400' />
                  <span className='text-sm font-medium'>Errors</span>
                </div>
                <div className='text-2xl font-bold text-red-400'>
                  {connectors.filter(c => c.status === 'error').length}
                </div>
              </div>

              <div className='bg-slate-800 p-4 rounded-lg'>
                <div className='flex items-center gap-2 mb-2'>
                  <Users className='h-5 w-5 text-blue-400' />
                  <span className='text-sm font-medium'>Total</span>
                </div>
                <div className='text-2xl font-bold text-blue-400'>{connectors.length}</div>
              </div>
            </div>
          )}
        </Card>

        {/* Connectors Section */}
        <Card className='mb-6'>
          <div
            className='flex items-center justify-between cursor-pointer'
            onClick={() => toggleSection('connectors')}
          >
            <h2 className='text-lg font-semibold flex items-center gap-2'>
              <Database className='h-5 w-5 text-green-400' />
              SFTP Connectors ({connectors.length})
            </h2>
            {expandedSections.has('connectors') ? (
              <ChevronDown className='h-5 w-5' />
            ) : (
              <ChevronRight className='h-5 w-5' />
            )}
          </div>

          {expandedSections.has('connectors') && (
            <div className='mt-4'>
              {loading ? (
                <div className='flex items-center justify-center py-8'>
                  <RefreshCw className='h-6 w-6 animate-spin text-slate-400' />
                  <span className='ml-2 text-slate-400'>Loading connectors...</span>
                </div>
              ) : error ? (
                <div className='bg-red-900/20 border border-red-700 rounded-lg p-4'>
                  <div className='text-red-200'>{error}</div>
                  <Button onClick={loadConnectors} variant='outline' className='mt-2'>
                    Retry
                  </Button>
                </div>
              ) : connectors.length === 0 ? (
                <div className='text-center py-8 text-slate-400'>
                  <Database className='h-12 w-12 mx-auto mb-4' />
                  <p>No connectors found</p>
                  <p className='text-sm'>Create your first SFTP connector to get started</p>
                </div>
              ) : (
                <div className='space-y-3'>
                  {connectors.map(connector => (
                    <div
                      key={connector.connectorId}
                      className={`bg-slate-800 rounded-lg p-4 border transition-colors ${
                        selectedConnector === connector.connectorId
                          ? 'border-blue-500 bg-slate-700'
                          : 'border-slate-700 hover:border-slate-600'
                      }`}
                      onClick={() => setSelectedConnector(connector.connectorId)}
                    >
                      <div className='flex items-center justify-between'>
                        <div className='flex items-center gap-3'>
                          <div className={`${getStatusColor(connector.status)}`}>
                            {getStatusIcon(connector.status)}
                          </div>
                          <div>
                            <h3 className='font-medium text-white'>{connector.name}</h3>
                            <p className='text-sm text-slate-400'>
                              {connector.host}:{connector.port} • {connector.username}
                            </p>
                          </div>
                        </div>

                        <div className='flex items-center gap-2'>
                          <Button
                            onClick={e => {
                              e.stopPropagation();
                              testConnection(connector.connectorId);
                            }}
                            variant='outline'
                          >
                            <TestTube className='h-4 w-4' />
                            Test
                          </Button>

                          <Button
                            onClick={e => {
                              e.stopPropagation();
                              handleFileAction('browse', connector.connectorId);
                            }}
                          >
                            <FolderOpen className='h-4 w-4' />
                            Browse
                          </Button>
                        </div>
                      </div>

                      {showAdvanced && (
                        <div className='mt-3 pt-3 border-t border-slate-700'>
                          <div className='grid grid-cols-2 gap-4 text-sm'>
                            <div>
                              <span className='text-slate-400'>S3 Bucket:</span>
                              <span className='ml-2 text-white'>
                                {connector.s3Bucket || 'Not set'}
                              </span>
                            </div>
                            <div>
                              <span className='text-slate-400'>S3 Prefix:</span>
                              <span className='ml-2 text-white'>
                                {connector.s3Prefix || 'Not set'}
                              </span>
                            </div>
                            <div>
                              <span className='text-slate-400'>AWS Connector ID:</span>
                              <span className='ml-2 text-white font-mono text-xs'>
                                {connector.awsConnectorId || 'Not set'}
                              </span>
                            </div>
                            <div>
                              <span className='text-slate-400'>Last Tested:</span>
                              <span className='ml-2 text-white'>
                                {connector.lastTested
                                  ? new Date(connector.lastTested).toLocaleString()
                                  : 'Never'}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* File Management Section */}
        {selectedConnector && (
          <Card className='mb-6'>
            <div
              className='flex items-center justify-between cursor-pointer'
              onClick={() => toggleSection('files')}
            >
              <h2 className='text-lg font-semibold flex items-center gap-2'>
                <FileText className='h-5 w-5 text-purple-400' />
                File Management
              </h2>
              {expandedSections.has('files') ? (
                <ChevronDown className='h-5 w-5' />
              ) : (
                <ChevronRight className='h-5 w-5' />
              )}
            </div>

            {expandedSections.has('files') && (
              <div className='mt-4'>
                <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
                  <Button
                    onClick={() => handleFileAction('browse', selectedConnector)}
                    className='flex items-center gap-2'
                  >
                    <FolderOpen className='h-4 w-4' />
                    Browse Files
                  </Button>

                  <Button
                    onClick={() => handleFileAction('upload', selectedConnector)}
                    variant='outline'
                    className='flex items-center gap-2'
                  >
                    <Upload className='h-4 w-4' />
                    Upload Files
                  </Button>

                  <Button
                    onClick={() => handleFileAction('download', selectedConnector)}
                    variant='outline'
                    className='flex items-center gap-2'
                  >
                    <Download className='h-4 w-4' />
                    Download All
                  </Button>

                  <Button
                    onClick={() => handleFileAction('sync', selectedConnector)}
                    variant='outline'
                    className='flex items-center gap-2'
                  >
                    <RefreshCw className='h-4 w-4' />
                    Sync Now
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Development Notes */}
        <Card className='bg-slate-800 border-slate-600'>
          <h2 className='text-lg font-semibold flex items-center gap-2 mb-4'>
            <Settings className='h-5 w-5 text-yellow-400' />
            Development Notes
          </h2>
          <div className='space-y-2 text-sm text-slate-300'>
            <p>• This is a hidden development page for Connector 2.0</p>
            <p>• Not included in main routing - accessible via direct URL</p>
            <p>• Work in progress - features will be added incrementally</p>
            <p>• Advanced mode shows additional connector details</p>
            <p>• File management actions are placeholders for now</p>
          </div>
        </Card>
      </div>
    </div>
  );
}
